import PlannerStartTime from '@/components/PlannerStartTime';
import { PageHeader } from "./_shared";
import { SegmentedControl } from "@/components/CommercialRegister";
import "@/components/crewPlanner.css";
import { InstructionEditor } from "@/components/ScheduleInstructions";
import { PlantLocation, PlantTimeline, PlantMoveBadge, PlantMoves } from "@/components/PlantAwareness";
// Crew Planner — simple weekly people scheduler. Jobs down the side, days
// across the top, drag INDIVIDUAL PEOPLE onto a job/day. Every action writes
// the existing db.assignments rows, so the field worker schedule updates
// automatically. Day Board (single-day dispatch) remains as the second view.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import PlannerAllocation, { PlannerCellDetail, ConflictList, plantName } from "@/components/PlannerAllocation";
import { nzTodayIso, shiftIsoDay } from "@/lib/dates";
import DayBoard, { isoDay } from "@/components/DayBoard";
import { weatherIcon } from "@/lib/weather";
import {
  ChevronLeft, ChevronRight, Plus, X, Search, Users as UsersIcon,
  AlertTriangle, PanelLeftClose, PanelLeftOpen, Truck, Filter,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";

// ---------- helpers ----------
const addDays = shiftIsoDay;
const mondayOf = (iso) => {
  const d = new Date(iso + "T00:00:00");
  d.setDate(d.getDate() - ((d.getDay() + 6) % 7));
  return isoDay(d);
};
const fmtShort = (iso) => new Date(iso + "T00:00:00").toLocaleDateString("en-NZ", { day: "numeric", month: "short" });
const dowShort = (iso) => new Date(iso + "T00:00:00").toLocaleDateString("en-NZ", { weekday: "short" }).toUpperCase();
const fmtLong = (iso) => new Date(iso + "T00:00:00").toLocaleDateString("en-NZ", { weekday: "long", day: "numeric", month: "long" });
const isSunday = (iso) => new Date(iso + "T00:00:00").getDay() === 0;
const leaveOn = (leave, uid, iso) => leave.find((l) => l.user_id === uid && l.start_date <= iso && l.end_date >= iso);
const initials = (name) => (name || "?").split(" ").map((p) => p[0]).join("").slice(0, 2).toUpperCase();
const ROLE_LABEL = { field: "Field", office: "Office", admin: "Admin", super_admin: "Platform" };
const roleOf = (u) => (u.truck_driver ? "Truck Driver" : ROLE_LABEL[u.role] || u.role);

const JOB_COLORS = ["#10B981", "#3B82F6", "#F97316", "#8B5CF6", "#14B8A6", "#EAB308", "#EC4899", "#EF4444", "#06B6D4", "#84CC16"];
const jobColor = (id) => {
  let h = 0;
  for (const c of id) h = (h * 31 + c.charCodeAt(0)) >>> 0;
  return JOB_COLORS[h % JOB_COLORS.length];
};

export default function CrewPlanner() {
  const [params, setParams] = useSearchParams();
  const view = params.get("view") === "day" ? "day" : "planner";
  const date = params.get("date") || nzTodayIso();
  const setParam = (k, v) => {
    const sp = new URLSearchParams(params);
    if (v == null) sp.delete(k); else sp.set(k, v);
    setParams(sp, { replace: true });
  };

  return (
    <div className="crew-planner p-4 md:p-6 w-full min-w-0" data-testid="crew-planner-page">
      <PageHeader tag="Operations" title="Crew Planner" subtitle="Plan people, crews and plant across the week." action={
        <SegmentedControl label="Planner view" value={view} onChange={v => setParam("view", v === "planner" ? null : "day")} options={[{key:"planner",label:"Planner",testId:"planner-toggle-planner"},{key:"day",label:"Day board",testId:"planner-toggle-dayboard"}]} testId="planner-view-toggle" />
      }/>

      {view === "day" ? (
        <DayBoard date={date} setDate={(d) => setParam("date", d)} />
      ) : (
        <WeekBoard focusJob={params.get("job_id")} date={date} setDate={(d) => setParam("date", d)} />
      )}
    </div>
  );
}

// ================= WEEKLY BOARD =================
function WeekBoard({ date, setDate, focusJob }) {
  const start = mondayOf(date);
  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [jobPage, setJobPage] = useState(1);
  const [jobSearch, setJobSearch] = useState('');
  const [resourceMode, setResourceMode] = useState('people');
  const [plantFilter,setPlantFilter] = useState('all');
  const [plantDetail,setPlantDetail] = useState(null);
  const [showMoves,setShowMoves] = useState(false);
  const [instruction,setInstruction] = useState(null);
  const [allocation, setAllocation] = useState(null);
  const [detail, setDetail] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [showConflicts, setShowConflicts] = useState(false);
  const [conflictsOnly, setConflictsOnly] = useState(false);
  const [resourceFilter, setResourceFilter] = useState('');
  const loadRef = useRef(null);
  const loadVersion = useRef(0);
  const [qJobs, setQJobs] = useState("");
  const [qPeople, setQPeople] = useState("");
  const [peopleFilter, setPeopleFilter] = useState("all");
  const [selectedDay, setSelectedDay] = useState(() => {
    const today = nzTodayIso();
    return today >= start && today <= addDays(start, 6) ? today : start;
  });
  useEffect(() => {
    const today = nzTodayIso();
    setSelectedDay(today >= start && today <= addDays(start, 6) ? today : start);
  }, [start]);
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem("fl_planner_sidebar") === "1"; } catch { return false; }
  });
  const toggleSidebar = () => {
    setCollapsed((v) => {
      try { localStorage.setItem("fl_planner_sidebar", v ? "0" : "1"); } catch { /* noop */ }
      return !v;
    });
  };
  const [multiSel, setMultiSel] = useState(new Set());
  const [menu, setMenu] = useState(null);       // {x, y, assignment, job, iso, user}
  const [hoverCell, setHoverCell] = useState(null);
  const [saving, setSaving] = useState(false);
  const [flash, setFlash] = useState(false);
  const flashT = useRef(null);
  const dragRef = useRef(null);

  useEffect(() => { const t = setTimeout(() => {setJobSearch(qJobs); setJobPage(1);}, 250); return () => clearTimeout(t); }, [qJobs]);
  const load = useCallback(async () => {
    loadRef.current?.abort();
    const controller = new AbortController(); loadRef.current = controller; setLoadError("");
    const version = ++loadVersion.current;
    try {
      const { data } = await api.get("/crew-planner", { params: { start, days: 7, q: jobSearch, page: jobPage, job_id: focusJob || undefined }, signal: controller.signal });
      if (version === loadVersion.current) setData(data);
    } catch (e) {
      if (!controller.signal.aborted) {setLoadError(e?.response?.data?.detail || "Failed to load planner"); toast.error(e?.response?.data?.detail || "Failed to load planner");}
    }
  }, [start, jobSearch, jobPage, focusJob]);
  useEffect(() => { load(); return () => loadRef.current?.abort(); }, [load]);

  const flashSaved = () => {
    setFlash(true);
    clearTimeout(flashT.current);
    flashT.current = setTimeout(() => setFlash(false), 1600);
  };

  // forecast for day headers (first geocoded job, Auckland fallback)
  const geoJob = useMemo(() => (data?.jobs || []).find((j) => j.site_lat && j.site_lng), [data]);
  const [forecast, setForecast] = useState(null);
  useEffect(() => {
    let m = true;
    api.get("/weather/forecast", { params: { lat: geoJob?.site_lat ?? -36.85, lng: geoJob?.site_lng ?? 174.76 } })
      .then((r) => m && setForecast(r.data)).catch(() => m && setForecast(null));
    return () => { m = false; };
  }, [geoJob?.site_lat, geoJob?.site_lng]);
  const fcByDate = useMemo(() => Object.fromEntries((forecast?.days || []).map((d) => [d.date, d])), [forecast]);

  const users = data?.users || [];
  const jobs = data?.jobs || [];
  const leave = data?.leave || [];
  const crews = data?.crews || [];
  const dates = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(start, i)), [start]);
  const userById = useMemo(() => Object.fromEntries(users.map((u) => [u.id, u])), [users]);
  const rowsByCell = useMemo(() => {
    const m = {};
    for (const a of (data?.assignments || [])) {
      if (!a.job_id) continue;
      (m[`${a.job_id}|${a.date}`] ||= []).push(a);
    }
    return m;
  }, [data]);
  const rowsByUserDate = useMemo(() => {
    const m = {};
    for (const a of (data?.assignments || [])) (m[`${a.user_id}|${a.date}`] ||= []).push(a);
    return m;
  }, [data]);
  const vehByCell = useMemo(() => {
    const m = {};
    for (const v of (data?.vehicle_assignments || [])) (m[`${v.job_id}|${v.date}`] ||= []).push(v);
    return m;
  }, [data]);

  const visJobs = jobs.filter(j => (!conflictsOnly || data?.conflicts?.some(c=>c.allocations.some(a=>a.job_id===j.id))) && (!resourceFilter || (data?.assignments || []).some(a=>a.job_id===j.id && a.user_id===resourceFilter) || (data?.vehicle_assignments || []).some(a=>a.job_id===j.id && a.vehicle_id===resourceFilter) || crews.some(c=>c.id===resourceFilter && (data?.assignments || []).some(a=>a.job_id===j.id && c.member_ids?.includes(a.user_id)))));
  const planningJobs = visJobs.filter(j=>j.planning_reasons?.length);
  const plantContext = data?.plant_awareness?.plants || {};
  const visiblePlant = (data?.fleet || []).filter(p=>{
    if(!plantName(p).toLowerCase().includes(qPeople.toLowerCase()) && !p.vehicle_type?.toLowerCase().includes(qPeople.toLowerCase()) && !p.fleet_number?.toLowerCase().includes(qPeople.toLowerCase())) return false;
    const rows=(data?.vehicle_assignments || []).filter(v=>v.vehicle_id===p.id && v.date===selectedDay);
    const ctx=plantContext[p.id];
    return plantFilter==='all' || (plantFilter==='maintenance' && ctx?.maintenance) || (plantFilter==='available' && !rows.length && !ctx?.maintenance) || (plantFilter==='allocated' && rows.length>0) || (plantFilter==='conflict' && (rows.length>1 || (rows.length && ctx?.maintenance))) || (plantFilter==='move_required' && ctx?.transitions?.some(t=>t.date===selectedDay && t.state==='move_required'));
  });
  const personStatus = (uid) => {
    const lv = leaveOn(leave, uid, selectedDay);
    if (data?.conflicts?.some(c=>c.resource_id===uid && c.date===selectedDay)) return {kind: "conflict", label: lv ? "Leave conflict" : "Schedule clash"};
    if (lv) return { kind: "leave", label: (lv.leave_type || "leave").replace("_", " ") };
    if ((rowsByUserDate[`${uid}|${selectedDay}`] || []).some(r=>!r.job_id)) return {kind:"dayoff",label:"Day off"};
    const rows = (rowsByUserDate[`${uid}|${selectedDay}`] || []).filter((r) => r.job_id);
    if (rows.length) return { kind: "assigned", label: rows.map((r) => jobs.find((j) => j.id === r.job_id)?.job_number || "job").join(" + ") };
    return { kind: "available", label: "Available" };
  };
  const qp = qPeople.trim().toLowerCase();
  const visPeople = users.filter((u) => {
    if (qp && !u.name.toLowerCase().includes(qp) && !roleOf(u).toLowerCase().includes(qp)) return false;
    if (peopleFilter === "all") return true;
    return personStatus(u.id).kind === peopleFilter;
  });

  // ---------- mutations ----------
  const assignOne = async (uid, jobId, iso) => {
    await api.put("/assignments", { user_id: uid, date: iso, job_id: jobId });
  };
  const removeRow = async (rowId) => api.delete(`/dispatch/assignment/${rowId}`);

  const doAssign = async (uids, jobId, iso, { moveFromRowIds = [] } = {}) => {
    setAllocation({uids, jobId, iso, moveFromRowIds});
  };

  const doRemove = async (row) => {
    setSaving(true);
    try {
      await removeRow(row.id);
      await load();
      flashSaved();
      toast.success(`${row.user_name?.split(" ")[0]} removed`, {
        action: { label: "Undo", onClick: () => assignOne(row.user_id, row.job_id, row.date).then(load) },
      });
    } catch { toast.error("Remove failed"); load(); }
    finally { setSaving(false); }
  };

  const doCopy = async (row, isoDates) => {
    setAllocation({uids:[row.user_id],jobId:row.job_id,iso:isoDates[0],dates:isoDates,sourceDate:row.date,sourcePersonal:row.scope_of_works || "",startTime:row.start_time || ""});
  };

  // ---------- drag & drop ----------
  const startDragPerson = (e, uid) => {
    const ids = multiSel.has(uid) && multiSel.size > 1 ? [...multiSel] : [uid];
    dragRef.current = { t: "people", uids: ids };
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", "fl-drag");
  };
  const startDragChip = (e, row) => {
    e.stopPropagation();
    dragRef.current = { t: "chip", row };
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", "fl-drag");
  };
  const dropOnCell = (jobId, iso) => (e) => {
    e.preventDefault();
    setHoverCell(null);
    const d = dragRef.current;
    dragRef.current = null;
    if (!d) return;
    if (d.t === "plant") setAllocation({jobId, iso, vehicleId: d.id});
    else if (d.t === "crew") setAllocation({jobId, iso, crewId: d.id});
    else if (d.t === "people") doAssign(d.uids, jobId, iso);
    else if (d.t === "chip") {
      const { row } = d;
      if (row.job_id === jobId && row.date === iso) return;
      setAllocation({uids:[row.user_id], jobId, iso, moveFromRowIds:[row.id], startTime:row.start_time || ""});
    }
  };

  const shiftWeek = (dir) => setDate(addDays(start, dir * 7));
  const rangeLabel = `${fmtShort(start)} – ${fmtShort(addDays(start, 6))} ${start.slice(0, 4)}`;
  const closeOverlays = () => { setMenu(null); };

  if (!data || data.start !== start) return <div className="p-6">{loadError || "Loading planner…"}{loadError && <Button onClick={load}>Retry</Button>}</div>;

  return (
    <div onClick={closeOverlays} className="bg-white dark:bg-[#0B1520] text-zinc-900 dark:text-slate-100 p-2">
      {loadError && <p role="alert" className="text-sm text-red-600 mb-2">{loadError} — displayed data may be stale. <button className="underline" onClick={load}>Retry</button></p>}
      {/* Week controls */}
      <div className="flex items-center gap-2 mb-3 flex-wrap" data-testid="planner-controls">
        <button onClick={toggleSidebar}
          className="h-9 w-9 hidden md:inline-flex items-center justify-center border border-zinc-300 dark:border-white/15 text-zinc-500 hover:text-[#FF5F15] hover:border-[#FF5F15]"
          title={collapsed ? "Show sidebar" : "Hide sidebar"} data-testid="planner-sidebar-toggle">
          {collapsed ? <PanelLeftOpen className="w-4 h-4" /> : <PanelLeftClose className="w-4 h-4" />}
        </button>
        <Button variant="outline" size="icon" className="rounded-sm h-9 w-9" onClick={() => shiftWeek(-1)} data-testid="planner-prev"><ChevronLeft className="w-4 h-4" /></Button>
        <Button variant="outline" size="sm" className="rounded-sm font-bold uppercase tracking-wider h-9" onClick={() => setDate(nzTodayIso())} data-testid="planner-today">Today</Button>
        <Button variant="outline" size="icon" className="rounded-sm h-9 w-9" onClick={() => shiftWeek(1)} data-testid="planner-next"><ChevronRight className="w-4 h-4" /></Button>
        <span className="font-display font-bold uppercase tracking-tight text-sm ml-1" data-testid="planner-range-label">{rangeLabel}</span>
        <div className="ml-auto flex items-center gap-3">
          {(saving || flash) && (
            <span className={`text-[11px] font-mono uppercase tracking-widest ${saving ? "text-zinc-400" : "text-emerald-600"}`} data-testid="planner-save-indicator">
              {saving ? "Saving…" : "✓ Saved"}
            </span>
          )}
        </div>
      </div>

      <div className="planner-toolbar">
        <div className="planner-toolbar-summary">
          <span>{data.job_total} Jobs · {users.length} people · {data.fleet.length} Plant</span>
          <Button size="sm" variant="outline" onClick={()=>setShowMoves(true)}>Plant moves · {data.plant_awareness?.moves?.length || 0}</Button>
          <Button size="sm" variant="outline" className={data.conflicts.length ? "planner-conflict" : ""} onClick={()=>setShowConflicts(true)}>{data.conflicts.length > 0 && <AlertTriangle className="w-4 h-4 mr-1"/>}{data.conflicts.length} conflicts</Button>
        </div>
        <div className="planner-toolbar-search">
          <label className="planner-search"><Search className="w-4 h-4 shrink-0"/><input aria-label="Search Jobs" placeholder="Search Jobs" value={qJobs} onChange={e=>setQJobs(e.target.value)}/></label>
          <Button variant="outline" size="sm" aria-expanded={showFilters} aria-controls="planner-filters" onClick={()=>setShowFilters(v=>!v)}><Filter className="w-4 h-4 mr-1"/>Filters{(conflictsOnly || resourceFilter) ? ' · Active' : ''}</Button>
          {focusJob && <Link to="/app/crew-planner" className="text-sm underline">All Jobs</Link>}
        </div>
      </div>
      {showFilters && <div id="planner-filters" className="planner-filter-panel">
        <label className="flex items-center gap-2"><input type="checkbox" checked={conflictsOnly} onChange={e=>setConflictsOnly(e.target.checked)}/> Conflicts only</label>
        <select aria-label="Filter resource" value={resourceFilter} onChange={e=>setResourceFilter(e.target.value)} className="border p-1 bg-white dark:bg-[#101e2e] max-w-48"><option value="">All resources</option><optgroup label="Crews">{crews.map(c=><option value={c.id} key={c.id}>{c.name}</option>)}</optgroup><optgroup label="People">{users.map(u=><option value={u.id} key={u.id}>{u.name}</option>)}</optgroup><optgroup label="Plant">{data.fleet.map(p=><option value={p.id} key={p.id}>{plantName(p)}</option>)}</optgroup></select>
      </div>}
      <div className="planner-pagination">
        <span>Jobs {data.job_total ? (data.job_page-1)*100+1 : 0}–{Math.min(data.job_page*100,data.job_total)} of {data.job_total}{(conflictsOnly || resourceFilter) && ` · ${visJobs.length} visible on this page`}</span>
        {data.job_pages > 1 && <><Button size="sm" variant="outline" disabled={data.job_page<=1} onClick={()=>setJobPage(data.job_page-1)}>Previous Jobs</Button><Button size="sm" variant="outline" disabled={data.job_page>=data.job_pages} onClick={()=>setJobPage(data.job_page+1)}>Next Jobs</Button></>}
      </div>
      {/* MOBILE: one day at a time */}
      <div className="md:hidden">
        <MobileDay dates={dates} selectedDay={selectedDay} setSelectedDay={d=>{setSelectedDay(d);setDate(d);}}
          jobs={visJobs} onInstruction={(jobId,iso)=>setInstruction({jobId,iso})} onPlant={setPlantDetail} plantByCell={vehByCell} data={data} onDetail={(jobId,iso)=>setDetail({jobId,iso})} rowsByCell={rowsByCell} fcByDate={fcByDate} userById={userById}
          onAdd={(jobId, iso, e) => { e.stopPropagation(); setAllocation({jobId, iso}); }}
          onChip={(e, row) => { e.stopPropagation(); const r = e.currentTarget.getBoundingClientRect(); setMenu({ x: r.left, y: r.bottom + 4, row }); }} />
      </div>

      {/* DESKTOP: sidebar + board */}
      <div className="hidden md:flex gap-3 items-start">
        {!collapsed && (
          <aside className="w-[240px] shrink-0 space-y-3" data-testid="planner-sidebar">
            {/* JOBS */}
            <div className="hard-card p-3">
              <div className="flex items-center justify-between mb-2">
                <div className="label-tag">Needs Planning ({planningJobs.length})</div>
                <Link to="/app/jobs" className="inline-flex items-center gap-0.5 text-[11px] font-bold uppercase tracking-wider text-zinc-500 hover:text-[#FF5F15]" data-testid="planner-add-job">
                  <Plus className="w-3 h-3" /> Add Job
                </Link>
              </div>
              <div className="flex items-center gap-1.5 border border-zinc-300 dark:border-white/15 px-2 h-8 mb-2">
                <Search className="w-3 h-3 text-zinc-400" />
                <input value={qJobs} onChange={(e) => setQJobs(e.target.value)} placeholder="Search jobs…"
                  className="bg-transparent outline-none text-[11px] w-full" data-testid="planner-jobs-search" />
              </div>
              <div className="space-y-1 max-h-[280px] overflow-y-auto">
                {planningJobs.map((j) => (
                  <div key={j.id} className="pl-2 pr-1.5 py-1.5 border-l-[3px] bg-zinc-50 dark:bg-white/[0.03]"
                    style={{ borderLeftColor: jobColor(j.id) }} data-testid={`planner-sidejob-${j.id}`}>
                    <div className="flex flex-wrap items-baseline gap-1.5">
                      <span className="font-mono text-[11px] font-bold" style={{ color: jobColor(j.id) }}>{j.job_number}</span>
                      <span className="planner-job-title w-full">{j.title}</span>
                      {j.progress != null && <span className="font-mono text-[11px] text-zinc-500">{j.progress}%</span>}
                    </div>
                    {j.site_address && (
                      <div className="text-xs text-zinc-500 line-clamp-2">{j.site_address}</div>
                    )}
                    <div className="text-[11px] mt-1">{j.planning_reasons.join(' · ')}</div>
                    <button className="planner-schedule-button" onClick={()=>setAllocation({jobId:j.id,iso:selectedDay})}>Schedule</button>
                    {j.progress != null && (
                      <div className="h-[3px] bg-zinc-200 dark:bg-white/10 mt-1">
                        <div className="h-full" style={{ width: `${j.progress}%`, background: jobColor(j.id) }} />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* AVAILABLE PEOPLE */}
            <div className="hard-card p-3" data-testid="planner-people-panel">
              <div className="flex items-center justify-between mb-1">
                <div className="label-tag">Resources</div>
                <span className="text-[11px] font-mono uppercase tracking-widest text-zinc-400">{dowShort(selectedDay)} {fmtShort(selectedDay)}</span>
              </div>
              <div className="planner-resource-tabs">{['crews','people','plant'].map(mode=><button key={mode} aria-pressed={resourceMode===mode} className={`text-xs p-1 border ${resourceMode===mode ? 'bg-[#FF5F15] text-white' : ''}`} onClick={()=>setResourceMode(mode)}>{mode.toUpperCase()}</button>)}</div>
              <div className="text-[11px] text-zinc-500 dark:text-slate-400 mb-2">Availability for selected day; no recorded allocation/leave = available.</div>
              <div className="flex items-center gap-1.5 border border-zinc-300 dark:border-white/15 px-2 h-8 mb-1.5">
                <Search className="w-3 h-3 text-zinc-400" />
                <input value={qPeople} onChange={(e) => setQPeople(e.target.value)} placeholder="Search resources…"
                  className="bg-transparent outline-none text-[11px] w-full" data-testid="planner-people-search" />
              </div>
              {resourceMode === "people" && <Select value={peopleFilter} onValueChange={setPeopleFilter}>
                <SelectTrigger className="h-7 rounded-sm text-[11px] mb-2" data-testid="planner-people-filter">
                  <Filter className="w-3 h-3 mr-1 text-zinc-400" /><SelectValue />
                </SelectTrigger>
                <SelectContent className="rounded-sm">
                  <SelectItem value="all">All people</SelectItem>
                  <SelectItem value="available">Available</SelectItem>
                  <SelectItem value="assigned">Assigned</SelectItem>
                  <SelectItem value="dayoff">Day off</SelectItem><SelectItem value="leave">On leave</SelectItem><SelectItem value="conflict">Conflict</SelectItem>
                </SelectContent>
              </Select>}
              {resourceMode==='plant' && <select aria-label="Plant state" className="w-full text-xs border mb-2 p-1 bg-white dark:bg-[#101e2e]" value={plantFilter} onChange={e=>setPlantFilter(e.target.value)}>{[['all','All Plant'],['available','Available'],['allocated','Allocated'],['move_required','Move required'],['conflict','Conflict'],['maintenance','Maintenance']].map(([v,label])=><option key={v} value={v}>{label}</option>)}</select>}
              <div className="space-y-0.5 max-h-[420px] overflow-y-auto">
                {resourceMode === 'crews' && crews.filter(c=>c.name.toLowerCase().includes(qp)).map(c=><div key={c.id} draggable onDragStart={e=>{dragRef.current={t:'crew',id:c.id};e.dataTransfer.setData('text/plain','fl-drag');}} className="p-2 border-b border-zinc-200 dark:border-slate-700 cursor-grab text-xs"><strong>{c.name}</strong><div>{c.member_ids?.filter(id=>userById[id]).length || 0} active people</div><div className="text-amber-600">{c.member_ids?.filter(id=>['leave','conflict'].includes(personStatus(id).kind)).length || 0} leave/conflict</div></div>)}
                {resourceMode === 'plant' && visiblePlant.map(p=>{const rows=data.vehicle_assignments.filter(v=>v.vehicle_id===p.id && v.date===selectedDay);return <div key={p.id} draggable onDragStart={e=>{dragRef.current={t:'plant',id:p.id};e.dataTransfer.setData('text/plain','fl-drag');}} className="p-2 border-b border-zinc-200 dark:border-slate-700 cursor-grab text-xs"><button className="font-bold text-left underline" onClick={()=>setPlantDetail(p.id)}>{p.fleet_number} {plantName(p)}</button><div>{p.vehicle_type} · {plantContext[p.id]?.maintenance ? 'Maintenance' : rows.length>1 ? 'Plant clash' : rows.length ? 'Allocated' : 'Available'}</div><PlantLocation context={plantContext[p.id]} compact/><PlantMoveBadge transition={plantContext[p.id]?.transitions?.find(t=>t.date===selectedDay)}/></div>})}
                {resourceMode === 'people' && visPeople.map((u) => {
                  const st = personStatus(u.id);
                  return (
                    <div key={u.id} draggable onDragStart={(e) => startDragPerson(e, u.id)}
                      className="group flex items-center gap-2 px-1.5 min-h-[48px] py-1 cursor-grab active:cursor-grabbing hover:bg-zinc-50 dark:hover:bg-white/5 select-none"
                      data-testid={`planner-person-${u.id}`}>
                      <input type="checkbox" checked={multiSel.has(u.id)}
                        onChange={() => setMultiSel((s) => { const n = new Set(s); n.has(u.id) ? n.delete(u.id) : n.add(u.id); return n; })}
                        onClick={(e) => e.stopPropagation()}
                        className={`accent-[#FF5F15] w-3 h-3 ${multiSel.has(u.id) ? "" : "opacity-0 group-hover:opacity-100 focus:opacity-100"}`}
                        data-testid={`planner-person-check-${u.id}`} />
                      <span className="w-6 h-6 bg-zinc-800 dark:bg-white/15 text-white rounded-full flex items-center justify-center text-[11px] font-display font-black shrink-0">
                        {initials(u.name)}
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="text-[13px] font-semibold truncate leading-tight">{u.name}</div>
                        <div className="text-[11px] font-mono uppercase tracking-wider text-zinc-400 truncate">{roleOf(u)}</div>
                      </div>
                      {st.kind === "available" && <span className="text-[11px] text-emerald-700 dark:text-emerald-300 shrink-0">Available</span>}
                      {st.kind === "assigned" && <span className="text-[11px] max-w-[70px] text-right text-zinc-500" title={`Assigned ${st.label}`}>→ {st.label}</span>}
                      {st.kind === "dayoff" && <span className="text-[11px]">Day off</span>}
                      {st.kind === "conflict" && <span className="text-[11px] text-amber-600">{st.label}</span>}
                      {st.kind === "leave" && <span className="text-[11px] font-bold uppercase text-blue-500 shrink-0">{st.label}</span>}
                    </div>
                  );
                })}
              </div>
            </div>
          </aside>
        )}

        {/* BOARD */}
        <div className="flex-1 min-w-0 hard-card overflow-auto max-h-[calc(100vh-210px)]" data-testid="planner-board">
          <div style={{ minWidth: 7 * 164 + 208 }}>
            {/* Day headers */}
            <div className="grid sticky top-0 z-30" style={{ gridTemplateColumns: `208px repeat(7, minmax(164px,1fr))` }}>
              <div className="planner-job-corner sticky left-0 z-10">Jobs</div>
              {dates.map((iso) => (
                <DayHeader key={iso} iso={iso} day={fcByDate[iso]} selected={iso === selectedDay}
                  onClick={() => {setSelectedDay(iso);setDate(iso);}} location={geoJob?.site_address} />
              ))}
            </div>

            {visJobs.length === 0 && (
              <div className="p-10 text-center text-sm text-zinc-500" data-testid="planner-no-jobs">
                No matching Jobs on this page. <Link to="/app/jobs" className="text-[#FF5F15] font-bold uppercase tracking-wider">Create a job →</Link>
              </div>
            )}

            {visJobs.map((j) => (
              <div key={j.id} className="grid border-b border-zinc-200 dark:border-white/10"
                style={{ gridTemplateColumns: `208px repeat(7, minmax(164px,1fr))` }}>
                {/* Job row header — sticky left */}
                <div className="planner-job-heading sticky left-0 z-20 bg-white dark:bg-[#0B1520] border-r border-zinc-200 dark:border-white/10 pl-2 pr-1.5 py-2"
                  style={{ boxShadow: `inset 3px 0 0 ${jobColor(j.id)}` }} data-testid={`planner-rowhead-${j.id}`}>
                  <div className="font-mono text-[11px] font-bold" style={{ color: jobColor(j.id) }}>{j.job_number}</div>
                  <div className="planner-job-title">{j.title}</div>
                  <Link className={data.needs[j.id] ? "planner-needs-badge" : "planner-needs-link"} to={`/app/jobs/${j.id}/work`}>{data.needs[j.id] ? `${data.needs[j.id]} open needs` : "Crew Needs"}</Link>
                  {j.progress != null && (
                    <div className="flex items-center gap-1 mt-1">
                      <div className="h-[3px] flex-1 bg-zinc-200 dark:bg-white/10">
                        <div className="h-full" style={{ width: `${j.progress}%`, background: jobColor(j.id) }} />
                      </div>
                      <span className="font-mono text-[11px] text-zinc-500">{j.progress}%</span>
                    </div>
                  )}
                </div>
                {dates.map((iso) => {
                  const rows = rowsByCell[`${j.id}|${iso}`] || [];
                  const key = `${j.id}|${iso}`;
                  const vehs = vehByCell[key] || [];
                  return (
                    <div key={iso} role="button" tabIndex={0} aria-label={`${j.title} ${iso} allocation details`}
                      onClick={()=>setDetail({jobId:j.id,iso})}
                      onKeyDown={e=>{if(e.target===e.currentTarget && (e.key==='Enter' || e.key===' ')){e.preventDefault();setDetail({jobId:j.id,iso});}}}
                      onDragOver={(e) => { e.preventDefault(); setHoverCell(key); }}
                      onDragLeave={() => setHoverCell((h) => (h === key ? null : h))}
                      onDrop={dropOnCell(j.id, iso)}
                      className={`planner-cell group border-l border-zinc-100 dark:border-white/5 p-1 min-h-[84px] transition-colors ${iso === nzTodayIso() ? "bg-orange-50/40 dark:bg-[#FF5F15]/[0.04]" : ""} ${hoverCell === key ? "bg-[#FF5F15]/10 ring-2 ring-inset ring-[#FF5F15]" : ""}`}
                      data-testid={`planner-cell-${j.id}-${iso}`}>
                      {hoverCell === key && dragRef.current && (
                        <div className="text-[11px] font-bold uppercase tracking-wider text-[#FF5F15] px-1 pb-0.5">
                          Drop {dragRef.current.t === "people" && dragRef.current.uids.length === 1 ? userById[dragRef.current.uids[0]]?.name?.split(" ")[0] : dragRef.current.t === "chip" ? dragRef.current.row.user_name?.split(" ")[0] : dragRef.current.t === "plant" ? "Plant" : dragRef.current.t === "crew" ? "crew" : `${dragRef.current.uids?.length} people`} here
                        </div>
                      )}
                      <div className={rows.length || vehs.length ? "planner-allocation-surface" : ""}>
                      {rows.length > 0 && (
                        <div className="flex items-center justify-between px-1 pb-0.5">
                          <span className="text-[11px] font-mono uppercase tracking-wider text-zinc-400">{rows.length} {rows.length === 1 ? "person" : "people"}</span>
                          {vehs.length > 0 && (
                            <span className="inline-flex items-center gap-0.5 text-[11px] font-mono text-zinc-400" title={vehs.map((v) => v.vehicle_rego).join(", ")}>
                              <Truck className="w-2.5 h-2.5" /> {vehs.length}
                            </span>
                          )}
                        </div>
                      )}
                      {rows.slice(0, rows.length === 3 ? 3 : 2).map((r) => {
                        const u = userById[r.user_id];
                        const lv = leaveOn(leave, r.user_id, iso);
                        const dbl = (rowsByUserDate[`${r.user_id}|${iso}`] || []).filter((x) => x.job_id).length > 1;
                        return (
                          <div key={r.id} role="button" tabIndex={0} aria-label={`${r.user_name} allocation actions`} onKeyDown={e=>{if(e.key==='Enter' || e.key===' '){e.preventDefault();e.stopPropagation();const rect=e.currentTarget.getBoundingClientRect();setMenu({x:rect.left,y:rect.bottom+4,row:r});}}} draggable onDragStart={(e) => startDragChip(e, r)}
                            onClick={(e) => { e.stopPropagation(); const rect = e.currentTarget.getBoundingClientRect(); setMenu({ x: rect.left, y: rect.bottom + 4, row: r }); }}
                            onContextMenu={(e) => { e.preventDefault(); e.stopPropagation(); setMenu({ x: e.clientX, y: e.clientY, row: r }); }}
                            className="planner-worker flex items-center gap-1.5 h-9 px-1 mb-0.5 bg-white dark:bg-white/[0.06] border border-zinc-200 dark:border-white/10 cursor-grab active:cursor-grabbing hover:border-[#FF5F15] select-none"
                            data-testid={`planner-chip-${r.id}`}>
                            <span className="w-5 h-5 bg-zinc-800 dark:bg-white/20 text-white rounded-full flex items-center justify-center text-[11px] font-display font-black shrink-0">
                              {initials(r.user_name)}
                            </span>
                            <div className="min-w-0 flex-1">
                              <div className="text-[13px] font-semibold truncate leading-tight">{r.user_name}</div>
                              <div className="text-[11px] font-mono uppercase tracking-wider text-zinc-400 truncate">{u ? roleOf(u) : ""}</div>
                            </div>
                            {r.scope_of_works && <span aria-label="Personal instruction" title="Personal instruction" className="text-sky-700 dark:text-sky-300 text-sm">▤</span>}
                            {lv && <AlertTriangle className="w-3 h-3 text-blue-500 shrink-0" title={`On approved ${(lv.leave_type || "leave").replace("_", " ")}`} />}
                            {dbl && !lv && <AlertTriangle className="w-3 h-3 text-red-500 shrink-0" title="Also assigned to another job this day" />}
                          </div>
                        );
                      })}
                      <button className={`block text-[11px] text-orange-700 dark:text-orange-300 px-1 py-1 ${data.crew_instructions?.some(n=>n.job_id===j.id&&n.date===iso&&n.text) ? "" : "planner-instruction-action"}`} onClick={e=>{e.stopPropagation();setInstruction({jobId:j.id,iso});}}>{data.crew_instructions?.some(n=>n.job_id===j.id&&n.date===iso&&n.text) ? '▤ Instructions' : '+ Crew instruction'}</button>
                      {rows.length>3 && <button className="planner-more" onClick={e=>{e.stopPropagation();setDetail({jobId:j.id,iso});}}>+{rows.length-2} people · Details</button>}
                      {vehs.map(v=><div key={v.id} className="text-[11px] px-1 py-1 border-t border-zinc-200 dark:border-slate-700"><button className="text-left" onClick={e=>{e.stopPropagation();setPlantDetail(v.vehicle_id);}}>PLANT · {plantName(data.fleet.find(p=>p.id===v.vehicle_id)) || v.vehicle_rego}</button><PlantMoveBadge transition={plantContext[v.vehicle_id]?.transitions?.find(t=>t.date===iso && t.to.job_id===j.id)}/>{plantContext[v.vehicle_id]?.maintenance && <span className="block text-amber-700 dark:text-amber-300">⚠ Maintenance conflict</span>}</div>)}
                      {data.conflicts.some(c=>c.date===iso && c.allocations.some(a=>a.job_id===j.id)) && <button className="planner-conflict planner-cell-conflict" onClick={e=>{e.stopPropagation();setDetail({jobId:j.id,iso});}}>⚠ CONFLICT · Details</button>}
                      </div>
                      <button
                        onClick={(e) => { e.stopPropagation(); setAllocation({jobId:j.id,iso}); }}
                        className="planner-allocate-action"
                        data-testid={`planner-add-person-${j.id}-${iso}`}>
                         + Allocate
                      </button>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      </div>

      {instruction && <InstructionEditor job={jobs.find(j=>j.id===instruction.jobId)} date={instruction.iso} allocation={instruction.allocation} crew={data.crew_instructions?.find(n=>n.job_id===instruction.jobId&&n.date===instruction.iso)} onClose={()=>setInstruction(null)} onSaved={load}/>}
      {plantDetail && <PlantTimeline plant={data.fleet.find(p=>p.id===plantDetail) || {id:plantDetail}} context={plantContext[plantDetail]} dates={dates} onClose={()=>setPlantDetail(null)}/>}
      {showMoves && <PlantMoves awareness={data.plant_awareness} onClose={()=>setShowMoves(false)}/>}
      {allocation && <PlannerAllocation request={allocation} data={data} onClose={()=>setAllocation(null)} onSaved={async()=>{await load();setMultiSel(new Set());}}/>}
      {detail && !allocation && <PlannerCellDetail cell={detail} data={data} onClose={()=>setDetail(null)} onAllocate={setAllocation} onInstruction={(row)=>{setInstruction({jobId:detail.jobId,iso:detail.iso,allocation:row});setDetail(null);}} onRefresh={load} onEdit={(e,row)=>{e.stopPropagation();const r=e.currentTarget.getBoundingClientRect();setDetail(null);setMenu({x:r.left,y:r.bottom+4,row});}}/>}
      {showConflicts && <Dialog open onOpenChange={setShowConflicts}><DialogContent className="bg-white dark:bg-[#101e2e] max-h-[85vh] overflow-auto"><DialogHeader><DialogTitle>Schedule conflicts · visible week</DialogTitle><DialogDescription>Same-day work may be an intentional split day; end times are not stored.</DialogDescription></DialogHeader><ConflictList conflicts={data.conflicts} onOpen={(jobId,iso)=>{setShowConflicts(false);setSelectedDay(iso);if(jobs.some(j=>j.id===jobId))setDetail({jobId,iso});else window.location.assign(`/app/crew-planner?job_id=${encodeURIComponent(jobId)}&date=${iso}`);}}/>{!data.conflicts.length && <p>No recorded conflicts.</p>}</DialogContent></Dialog>}
      {/* Floating chip menu */}
      {menu && (
        <ChipMenu menu={menu} dates={dates} onClose={() => setMenu(null)}
          userById={userById} jobs={jobs}
          onInstruction={(row)=>{setMenu(null);setInstruction({jobId:row.job_id,iso:row.date,allocation:row});}}
          onRemove={(row) => { setMenu(null); doRemove(row); }}
          onCopy={(row, ds) => { setMenu(null); doCopy(row, ds); }}
          onStartTime={async (row, st) => {
            try { await api.patch(`/assignments/${row.id}`, { start_time: st || null }); load(); flashSaved(); }
            catch { toast.error("Couldn't save time"); }
          }} />
      )}


    </div>
  );
}

function DayHeader({ iso, day, selected, onClick, location }) {
  const Icon = day ? weatherIcon(day) : null;
  const today = iso === nzTodayIso();
  const heavy = day && (day.severity === "red");
  const title = day
    ? `${location || "Regional forecast"}\n${day.label} · ${day.rain_prob || 0}% chance · ${(day.rain_mm || 0).toFixed(1)}mm rain\nWind ${(day.wind_kmh || 0).toFixed(0)}km/h · ${Math.round(day.temp_min ?? 0)}–${Math.round(day.temp_max ?? 0)}°`
    : undefined;
  return (
    <button onClick={onClick} title={title} aria-pressed={selected}
      className={`planner-day-header bg-white dark:bg-[#0B1520] border-b border-l border-zinc-900 dark:border-white/20 dark:border-l-white/10 border-l-zinc-100 px-1 py-1.5 text-center relative ${today ? "border-t-[3px] border-t-[#FF5F15]" : ""} ${selected ? "bg-zinc-50 dark:bg-white/[0.05]" : ""}`}
      data-testid={`planner-day-${iso}`}>
      <div className={`text-[11px] font-bold uppercase tracking-widest ${today ? "text-[#FF5F15]" : isSunday(iso) ? "text-zinc-400 italic" : "text-zinc-600 dark:text-zinc-300"}`}>
        {dowShort(iso)} <span className="block text-[14px] font-semibold tracking-normal">{fmtShort(iso)}{today && " · Today"}</span>
      </div>
      {day ? (
        <div className="flex items-center justify-center gap-1.5 mt-0.5 text-[11px] font-mono text-zinc-500">
          {Icon && <Icon className={`w-3.5 h-3.5 ${day.severity === "red" ? "text-red-500" : day.severity === "amber" ? "text-amber-500" : "text-zinc-400"}`} />}
          <span>{Math.round(day.temp_max ?? 0)}°</span>
          <span>{(day.rain_mm || 0).toFixed(1)}mm</span>
        </div>
      ) : (
        <div className="text-[11px] font-mono text-zinc-300 dark:text-zinc-600 mt-0.5">—</div>
      )}
      {heavy && (
        <div className="text-[11px] font-bold uppercase tracking-wider text-red-500 flex items-center justify-center gap-0.5">
          <AlertTriangle className="w-2.5 h-2.5" /> {day.label}
        </div>
      )}
    </button>
  );
}

// small floating context menu for an assignment chip
function ChipMenu({ menu, dates, onClose, userById, jobs, onRemove, onCopy, onStartTime, onInstruction }) {
  const { row } = menu;
  const u = userById[row.user_id];
  const job = jobs.find((j) => j.id === row.job_id);
  const idx = dates.indexOf(row.date);
  const restOfWeek = dates.slice(idx + 1);
  const [st, setSt] = useState(row.start_time || "");
  const style = {
    left: Math.min(menu.x, window.innerWidth - 260),
    top: Math.min(menu.y, window.innerHeight - 300),
  };
  return (
    <div className="fixed z-50 w-[240px] max-h-[calc(100dvh-1rem)] overflow-y-auto hard-card bg-white dark:bg-[#101620] p-3 shadow-xl" style={style}
      onClick={(e) => e.stopPropagation()} data-testid="chip-menu">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-[12px] font-bold leading-tight">{row.user_name}</div>
          <div className="text-[11px] font-mono uppercase tracking-wider text-zinc-400">{u ? roleOf(u) : ""}</div>
          <div className="text-[11px] text-zinc-500 mt-1">{fmtLong(row.date)}</div>
          <div className="text-[11px] font-semibold" style={{ color: jobColor(row.job_id) }}>{job?.job_number} · {row.job_title}</div>
        </div>
        <button onClick={onClose} className="text-zinc-400 hover:text-zinc-900 dark:hover:text-white" data-testid="chip-menu-close"><X className="w-3.5 h-3.5" /></button>
      </div>
      <div className="mt-2">
        <PlannerStartTime value={st} onChange={time => { setSt(time); onStartTime(row, time); }} testId="chip-menu-start-time" />
        {!st && job?.start_time && <span className="text-[11px] font-mono text-zinc-400">default {job.start_time}</span>}
      </div>
      <div className="mt-2 border-t border-zinc-200 dark:border-white/10 pt-1.5 space-y-0.5">
        <MenuBtn onClick={()=>onInstruction(row)}>{row.scope_of_works ? "Edit personal instruction" : "Add personal instruction"}</MenuBtn>
        {idx < dates.length - 1 && (
          <MenuBtn onClick={() => onCopy(row, [dates[idx + 1]])} testid="chip-menu-copy-next">Copy to next day</MenuBtn>
        )}
        {restOfWeek.length > 0 && (
          <MenuBtn onClick={() => onCopy(row, restOfWeek)} testid="chip-menu-copy-week">Copy to rest of week</MenuBtn>
        )}
        <MenuBtn testid="chip-menu-move" onClick={onClose}>Move — drag the chip to another cell</MenuBtn>
        <MenuBtn danger onClick={() => onRemove(row)} testid="chip-menu-remove">Remove from this day</MenuBtn>
      </div>
    </div>
  );
}
const MenuBtn = ({ children, onClick, danger, testid }) => (
  <button onClick={onClick} data-testid={testid}
    className={`w-full text-left px-1.5 py-1 text-[11px] font-semibold hover:bg-zinc-100 dark:hover:bg-white/5 ${danger ? "text-red-600" : ""}`}>
    {children}
  </button>
);

// mobile: one day at a time
function MobileDay({ onInstruction, onPlant, plantByCell, data, onDetail, dates, selectedDay, setSelectedDay, jobs, rowsByCell, fcByDate, userById, onAdd, onChip }) {
  const idx = dates.indexOf(selectedDay);
  const day = fcByDate[selectedDay];
  const Icon = day ? weatherIcon(day) : null;
  return (
    <div data-testid="planner-mobile">
      <div className="flex items-center gap-2 mb-3">
        <Button variant="outline" size="icon" className="rounded-sm h-9 w-9" disabled={idx <= 0}
          onClick={() => setSelectedDay(dates[idx - 1])}><ChevronLeft className="w-4 h-4" /></Button>
        <div className="flex-1 text-center">
          <div className="font-display font-bold uppercase tracking-tight text-sm">{fmtLong(selectedDay)}</div>
          {day && (
            <div className="flex items-center justify-center gap-1.5 text-[11px] font-mono text-zinc-500">
              {Icon && <Icon className="w-3.5 h-3.5" />} {Math.round(day.temp_max ?? 0)}° · {(day.rain_mm || 0).toFixed(1)}mm
            </div>
          )}
        </div>
        <Button variant="outline" size="icon" className="rounded-sm h-9 w-9" disabled={idx >= dates.length - 1}
          onClick={() => setSelectedDay(dates[idx + 1])}><ChevronRight className="w-4 h-4" /></Button>
      </div>
      <div className="space-y-2">
        {jobs.map((j) => {
          const rows = rowsByCell[`${j.id}|${selectedDay}`] || [];
          return (
            <div key={j.id} className="hard-card p-3" style={{ boxShadow: `inset 3px 0 0 ${jobColor(j.id)}` }} data-testid={`planner-mobile-job-${j.id}`}>
              <div className="flex items-baseline gap-2">
                <span className="font-mono text-[11px] font-bold" style={{ color: jobColor(j.id) }}>{j.job_number}</span>
                <span className="text-sm font-bold truncate flex-1">{j.title}</span>
                <span className="text-[11px] font-mono text-zinc-500">{rows.length} people</span>
              </div>
              <div className="mt-1.5 space-y-0.5">
                {rows.slice(0, rows.length === 3 ? 3 : 2).map((r) => {
                  const u = userById[r.user_id];
                  return (
                    <button key={r.id} onClick={(e) => onChip(e, r)}
                      className="w-full flex items-center gap-2 h-8 px-1 bg-zinc-50 dark:bg-white/[0.05] border border-zinc-200 dark:border-white/10 text-left">
                      <span className="w-5 h-5 bg-zinc-800 dark:bg-white/20 text-white rounded-full flex items-center justify-center text-[11px] font-display font-black">{initials(r.user_name)}</span>
                      <span className="text-[11px] font-semibold truncate flex-1">{r.user_name}</span>
                      <span className="text-[11px] font-mono uppercase text-zinc-400">{u ? roleOf(u) : ""}</span>
                    </button>
                  );
                })}
              </div>
              {rows.length>3 && <div className="text-xs">+{rows.length-2} people</div>}
              {(plantByCell[`${j.id}|${selectedDay}`] || []).map(p=><div key={p.id} className="text-xs mt-1"><button className="text-left underline" onClick={()=>onPlant(p.vehicle_id)}>PLANT · {plantName(data.fleet.find(v=>v.id===p.vehicle_id)) || p.vehicle_rego}</button><PlantMoveBadge transition={data.plant_awareness?.plants?.[p.vehicle_id]?.transitions?.find(t=>t.date===selectedDay && t.to.job_id===j.id)}/>{data.plant_awareness?.plants?.[p.vehicle_id]?.maintenance && <span className="block text-amber-700 dark:text-amber-300">⚠ Maintenance conflict</span>}</div>)}
              {data.conflicts.some(c=>c.date===selectedDay && c.allocations.some(a=>a.job_id===j.id)) && <p className="text-xs text-amber-600">⚠ Conflict</p>}
              <button className="block text-xs text-orange-700 dark:text-orange-300 my-2" onClick={()=>onInstruction(j.id,selectedDay)}>{data.crew_instructions?.some(n=>n.job_id===j.id&&n.date===selectedDay&&n.text) ? '▤ Crew instruction' : '+ Crew instruction'}</button>
              <button className="text-xs underline mr-3" onClick={()=>onDetail(j.id,selectedDay)}>Details</button>
              <button onClick={(e) => onAdd(j.id, selectedDay, e)}
                className="mt-1 text-[11px] font-bold uppercase tracking-wider text-zinc-400 hover:text-[#FF5F15]">
                + Allocate resources
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
